import { cn } from '@/lib/utils';
import {
  CaretDown as CaretDownIcon,
  CaretUp as CaretUpIcon,
  WarningOctagon as WarningOctagonIcon,
  MagnifyingGlass as MagnifyingGlassIcon,
  Rows as RowsIcon,
  StackSimple as StackSimpleIcon,
  CalendarBlank as CalendarBlankIcon,
  ArrowRight as ArrowRightIcon,
} from '@phosphor-icons/react';
import { FlagPennant as FlagPennantIcon } from '@phosphor-icons/react';
import type { Provenance, Product, Customer, Snapshot } from '../data';
import {
  SNAPSHOTS,
  PRODUCTS,
  WAREHOUSES,
  REGION_LABEL,
  findWh,
  whColor,
} from '../data';

// ── Snapshot pill ──

export function SnapshotPill({ snap }: { snap: Snapshot }) {
  const isWeekly = snap.kind === 'weekly';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
        isWeekly
          ? 'border border-stone-300 bg-white text-stone-900 shadow-xs'
          : 'border border-transparent bg-stone-100 text-stone-700',
      )}
    >
      {isWeekly && <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />}
      <span className={isWeekly ? 'text-xs' : 'text-[11px]'}>
        {snap.shortLabel}
      </span>
      {isWeekly && (
        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
          anchor
        </span>
      )}
    </span>
  );
}

// ── Diff cell ──

export function DiffCell({ cur, prev }: { cur: number; prev: number }) {
  const delta = cur - prev;
  if (delta === 0) {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }
  const isUp = delta > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-px text-xs font-semibold tabular-nums',
        isUp
          ? 'border border-emerald-200 bg-emerald-50 text-emerald-800'
          : 'border border-red-200 bg-red-50 text-red-800',
      )}
    >
      {isUp ? '+' : ''}
      {delta}
    </span>
  );
}

// ── Provenance chips ──

export function ProvChips({ prov }: { prov: Provenance[] }) {
  const total = prov.reduce((s, p) => s + p.qty, 0);
  return (
    <span className="inline-flex flex-nowrap gap-1">
      {prov.map((p, i) => {
        const wh = findWh(p.wh);
        const pct = Math.round((p.qty / total) * 100);
        return (
          <span
            key={i}
            title={`${wh.name} — ${wh.loc}`}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded border border-stone-200 bg-stone-50 px-1.5 py-px text-[11px] font-medium text-stone-800 tabular-nums"
          >
            <span
              className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-[9px] font-bold text-white"
              style={{ background: whColor(p.wh) }}
            >
              {wh.short[0]}
            </span>
            <span>{p.wh}</span>
            <span className="text-muted-foreground">{p.qty}</span>
            {prov.length > 1 && (
              <span className="text-[10px] text-muted-foreground">
                · {pct}%
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

// ── Remainder cell ──

export function RemainderCell({
  remainder,
  uom,
}: {
  remainder: number;
  uom: string;
}) {
  if (remainder < 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-100 px-2 py-px text-[11px] font-semibold tabular-nums text-red-700">
        <WarningOctagonIcon size={11} />
        {remainder} {uom}
      </span>
    );
  }
  if (remainder === 0) {
    return (
      <span className="text-xs tabular-nums text-muted-foreground">
        0 {uom}
      </span>
    );
  }
  return (
    <span className="text-xs tabular-nums text-stone-900">
      {remainder} <span className="text-muted-foreground">{uom}</span>
    </span>
  );
}

// ── Customer & Product cells ──

export function CustomerCell({
  cust,
  dense,
}: {
  cust: Customer;
  dense: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="flex-shrink-0 font-mono text-[10px] text-muted-foreground">
          {cust.id}
        </span>
        <span className="truncate text-[13px] font-medium text-stone-900">
          {cust.name}
        </span>
      </div>
      {!dense && (
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <span>{cust.loc}</span>
          <span>·</span>
          <span>{cust.regionLabel}</span>
        </div>
      )}
    </div>
  );
}

export function ProductCell({
  prod,
  dense,
}: {
  prod: Product;
  dense: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="flex-shrink-0 font-mono text-[10px] text-muted-foreground">
          {prod.id}
        </span>
        <span className="truncate text-[13px] font-medium text-stone-900">
          {prod.name}
        </span>
      </div>
      {!dense && (
        <div className="font-mono text-[11px] text-muted-foreground">
          {prod.sku}
        </div>
      )}
    </div>
  );
}

// ── Status cell ──

const TAG_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  shortage: { bg: 'bg-amber-100', fg: 'text-amber-800', label: 'Shortage' },
  rebalance: { bg: 'bg-blue-100', fg: 'text-blue-800', label: 'Rebalanced' },
  tier: { bg: 'bg-indigo-100', fg: 'text-indigo-800', label: 'Tier hold' },
  order: { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'PO match' },
  rounding: { bg: 'bg-stone-100', fg: 'text-stone-700', label: 'Anchor roll' },
  forecast: { bg: 'bg-pink-100', fg: 'text-pink-800', label: 'Forecast' },
  manual: { bg: 'bg-stone-100', fg: 'text-stone-800', label: 'Manual' },
};

export function StatusCell({
  row,
  isChanged,
  isFlagged,
}: {
  row: { reason: { tag: string }; updated: string };
  isChanged: boolean;
  isFlagged: boolean;
}) {
  if (isFlagged) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
        <FlagPennantIcon size={11} weight="fill" />
        Flagged
      </span>
    );
  }
  if (!isChanged) {
    return <span className="text-[11px] text-muted-foreground">No change</span>;
  }
  const s = TAG_STYLE[row.reason.tag] || TAG_STYLE.manual;
  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          'inline-flex w-fit rounded px-1.5 py-px text-[10px] font-semibold tracking-wide',
          s.bg,
          s.fg,
        )}
      >
        {s.label}
      </span>
      <span className="text-[10px] text-muted-foreground">{row.updated}</span>
    </div>
  );
}

// ── Pivot switcher ──

const PIVOT_OPTS = [
  { id: 'list', Icon: RowsIcon, label: 'List' },
  { id: 'grouped', Icon: StackSimpleIcon, label: 'Grouped' },
  { id: 'horizon', Icon: CalendarBlankIcon, label: 'Calendar' },
];

export function PivotSwitcher({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        View
      </span>
      <div
        className="inline-flex gap-px rounded-lg border border-border bg-white p-0.5 shadow-xs"
        style={{ height: 32 }}
      >
        {PIVOT_OPTS.map((o) => {
          const isActive = value === o.id;
          return (
            <button
              key={o.id}
              onClick={() => onChange(o.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition-colors',
                isActive
                  ? 'bg-stone-900 text-white font-semibold'
                  : 'text-stone-700 hover:bg-stone-100',
              )}
            >
              <o.Icon size={14} />
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Snapshot comparator ──

function SnapSelect({
  value,
  onChange,
  dim,
}: {
  value: string;
  onChange: (v: string) => void;
  dim: string;
}) {
  const cur = SNAPSHOTS.find((s) => s.id === value)!;
  const isWeekly = cur.kind === 'weekly';
  return (
    <label className="inline-flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {dim}
      </span>
      <span className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 min-w-[200px] cursor-pointer appearance-none rounded-md border border-border bg-white px-2.5 pr-7 text-xs font-medium text-stone-900 shadow-xs"
        >
          {SNAPSHOTS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.kind === 'weekly' ? '◆ ' : ''}
              {s.label}
            </option>
          ))}
        </select>
        <CaretDownIcon
          size={11}
          className="pointer-events-none absolute right-2 top-2.5 text-muted-foreground"
        />
        {isWeekly && (
          <span className="pointer-events-none absolute right-6 top-1.5 rounded border border-indigo-200 bg-indigo-50 px-1.5 py-px text-[8px] font-bold uppercase tracking-wider text-indigo-700">
            Anchor
          </span>
        )}
      </span>
    </label>
  );
}

export function SnapshotComparator({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (v: { from: string; to: string }) => void;
}) {
  return (
    <div className="inline-flex items-end gap-2 rounded-lg border border-border bg-white p-1.5 px-2.5 shadow-xs">
      <SnapSelect
        dim="Compare from"
        value={from}
        onChange={(v) => onChange({ from: v, to })}
      />
      <span className="px-0.5 py-1.5 text-muted-foreground">
        <ArrowRightIcon size={14} className="text-muted-foreground" />
      </span>
      <SnapSelect
        dim="To"
        value={to}
        onChange={(v) => onChange({ from, to: v })}
      />
    </div>
  );
}

// ── Filter bar ──

export function FilterBar({
  filters,
  setFilters,
  total,
  shown,
}: {
  filters: Record<string, any>;
  setFilters: (f: any) => void;
  total: number;
  shown: number;
}) {
  const update = (patch: Record<string, any>) =>
    setFilters({ ...filters, ...patch });

  return (
    <div className="flex items-center gap-2 rounded-t-xl border border-b-0 border-border bg-white px-2.5 py-2">
      {/* Scope toggle */}
      <div className="inline-flex gap-px rounded-lg bg-stone-100 p-0.5">
        {[
          { id: 'mine', label: 'My customers' },
          { id: 'all', label: 'All customers' },
        ].map((b) => (
          <button
            key={b.id}
            onClick={() => update({ scope: b.id })}
            className={cn(
              'whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium',
              filters.scope === b.id
                ? 'bg-white text-stone-900 font-semibold shadow-xs'
                : 'text-muted-foreground',
            )}
          >
            {b.label}
          </button>
        ))}
      </div>

      <div className="h-5 w-px bg-border" />

      <select
        value={filters.region}
        onChange={(e) => update({ region: e.target.value })}
        className="h-7 w-[130px] cursor-pointer appearance-none rounded-md border border-border bg-white px-2.5 pr-6 text-xs text-stone-900"
      >
        <option value="all">All regions</option>
        {(['NE', 'SE', 'MW', 'SW', 'W'] as const).map((r) => (
          <option key={r} value={r}>
            {REGION_LABEL[r]}
          </option>
        ))}
      </select>

      <select
        value={filters.product}
        onChange={(e) => update({ product: e.target.value })}
        className="h-7 w-[150px] cursor-pointer appearance-none rounded-md border border-border bg-white px-2.5 pr-6 text-xs text-stone-900"
      >
        <option value="all">All products</option>
        {PRODUCTS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.id} · {p.name}
          </option>
        ))}
      </select>

      <select
        value={filters.warehouse}
        onChange={(e) => update({ warehouse: e.target.value })}
        className="h-7 w-[150px] cursor-pointer appearance-none rounded-md border border-border bg-white px-2.5 pr-6 text-xs text-stone-900"
      >
        <option value="all">All warehouses</option>
        {WAREHOUSES.map((w) => (
          <option key={w.id} value={w.id}>
            {w.id} · {w.loc}
          </option>
        ))}
      </select>

      <label className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap px-1 text-xs text-stone-800">
        <input
          type="checkbox"
          checked={filters.onlyChanged}
          onChange={(e) => update({ onlyChanged: e.target.checked })}
          className="accent-stone-900"
        />
        Changed
      </label>
      <label className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap text-xs text-stone-800">
        <input
          type="checkbox"
          checked={filters.onlyOverConsumed}
          onChange={(e) => update({ onlyOverConsumed: e.target.checked })}
          className="accent-stone-900"
        />
        Over-consumed
      </label>

      <div className="flex-1" />

      <div className="relative">
        <MagnifyingGlassIcon
          size={12}
          className="pointer-events-none absolute left-2 top-2 text-muted-foreground"
        />
        <input
          placeholder="Search customer or product…"
          value={filters.q}
          onChange={(e) => update({ q: e.target.value })}
          className="h-7 w-48 rounded-md border border-border bg-white pl-7 pr-2.5 text-xs outline-none"
        />
      </div>

      <span className="whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
        {shown}/{total} rows
      </span>
    </div>
  );
}
